import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format, formatDistanceToNow, parse, isValid } from 'date-fns';
import type { FormStatus, IncidentSeverity, UserRole } from '@/shared/types';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Parse a date value that may arrive as ISO string, JS Date, or mobile DD.MM.YYYY / DD.MM.YYYY HH:mm */
function parseAnyDate(date: string | Date): Date | null {
  if (date instanceof Date) return isValid(date) ? date : null;

  // Already formatted by mobile app → parse explicitly
  if (/^\d{2}\.\d{2}\.\d{4} \d{2}:\d{2}$/.test(date)) {
    const d = parse(date, 'dd.MM.yyyy HH:mm', new Date());
    return isValid(d) ? d : null;
  }
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(date)) {
    const d = parse(date, 'dd.MM.yyyy', new Date());
    return isValid(d) ? d : null;
  }

  // ISO / other standard formats
  const d = new Date(date);
  return isValid(d) ? d : null;
}

export function formatDate(date: string | Date | undefined, fmt = 'dd.MM.yyyy'): string {
  if (!date) return '—';
  const d = parseAnyDate(date);
  if (!d) return typeof date === 'string' ? date : '—';
  try { return format(d, fmt); } catch { return String(date); }
}

export function formatDateTime(date: string | Date | undefined): string {
  if (!date) return '—';
  const d = parseAnyDate(date);
  if (!d) return typeof date === 'string' ? date : '—';
  try { return format(d, 'dd.MM.yyyy HH:mm'); } catch { return String(date); }
}

export function formatRelative(date: string | Date | undefined): string {
  if (!date) return '—';
  const d = parseAnyDate(date);
  if (!d) return '—';
  try { return formatDistanceToNow(d, { addSuffix: true }); } catch { return '—'; }
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** Czas trwania z milisekund → „12 min" / „5 h 20 min" / „3 dni 4 h". */
export function formatDuration(ms: number): string {
  if (!isFinite(ms) || ms < 0) return '—';
  const totalMin = Math.round(ms / 60000);
  if (totalMin < 60) return `${totalMin} min`;
  const totalH = Math.floor(totalMin / 60);
  if (totalH < 24) { const m = totalMin % 60; return m ? `${totalH} h ${m} min` : `${totalH} h`; }
  const d = Math.floor(totalH / 24); const h = totalH % 24;
  const dLabel = `${d} ${d === 1 ? 'dzień' : 'dni'}`;
  return h ? `${dLabel} ${h} h` : dLabel;
}

/** Czas trwania z godzin (do statystyk zwracających godziny). */
export function formatHours(hours: number | null | undefined): string {
  if (hours == null || !isFinite(hours)) return '—';
  return formatDuration(hours * 3_600_000);
}

export const STATUS_CONFIG: Record<FormStatus, { label: string; color: string; bg: string }> = {
  DRAFT:     { label: 'Szkic',        color: 'text-slate-600',   bg: 'bg-slate-100'   },
  SAVED:     { label: 'Szkic',        color: 'text-sky-700',     bg: 'bg-sky-100'     },
  SUBMITTED: { label: 'Oczekujące',   color: 'text-amber-700',   bg: 'bg-amber-100'   },
  RETURNED:  { label: 'Zwrócone',     color: 'text-red-700',     bg: 'bg-red-100'     },
  APPROVED:  { label: 'Zatwierdzone', color: 'text-emerald-700', bg: 'bg-emerald-100' },
  REALIZED:  { label: 'Zrealizowane', color: 'text-teal-700',    bg: 'bg-teal-100'    },
  CLOSED:    { label: 'Zarchiwizowane', color: 'text-gray-600',  bg: 'bg-gray-100'    },
  CANCELLED: { label: 'Anulowane',    color: 'text-rose-700',    bg: 'bg-rose-100'    },
};

export const SEVERITY_CONFIG: Record<IncidentSeverity, { label: string; color: string; bg: string; dot: string }> = {
  LOW:      { label: 'Niski',    color: 'text-emerald-700', bg: 'bg-emerald-100', dot: 'bg-emerald-500' },
  MEDIUM:   { label: 'Średni',   color: 'text-amber-700',   bg: 'bg-amber-100',   dot: 'bg-amber-500'   },
  HIGH:     { label: 'Wysoki',   color: 'text-red-700',     bg: 'bg-red-100',     dot: 'bg-red-500'     },
  CRITICAL: { label: 'Krytyczny', color: 'text-purple-700', bg: 'bg-purple-100',  dot: 'bg-purple-500'  },
};

export const ROLE_CONFIG: Record<UserRole, { label: string; color: string }> = {
  ADMIN:      { label: 'Administrator', color: 'text-purple-600' },
  DISPATCHER: { label: 'Kierownik zmiany', color: 'text-blue-600' },
  INSTRUCTOR: { label: 'Instruktor',   color: 'text-green-600'  },
  ANALYST:    { label: 'Analityk',     color: 'text-orange-600' },
  INSURANCE:  { label: 'Specjalista ds. ubezpieczeń', color: 'text-sky-600' },
};

export function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * SEC/§8: ucieczka wartości WSTAWIANEJ DO HTML-a (np. dymki Leafletu, które nie
 * są Reactem i nie escapują same). Bez tego wartość wpisana przez operatora
 * (nazwisko, nazwa czynności, rejestracja) w rodzaju `<img src=x onerror=…>`
 * wykonałaby skrypt w sesji każdego, kto otworzy dymek. React w JSX escapuje
 * sam — to jest dla ścieżek `innerHTML`/`bindPopup`/`bindTooltip`.
 */
export function escapeHtml(value: unknown): string {
  return String(value ?? '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
}

export function buildQueryString(params: Record<string, any>): string {
  const filtered = Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null);
  return new URLSearchParams(Object.fromEntries(filtered)).toString();
}

/**
 * B-257: czy formularz wolno jeszcze zmieniać — **wyłącznie po statusie**.
 *
 * Świadomie osobna funkcja od `canEditSubmission` niżej, która dorzuca rolę:
 * kierownik i administrator mogą tam edytować formularz także po wysłaniu i po
 * zatwierdzeniu (B-318). Bramka oświadczeń na serwerze patrzy tylko na status,
 * więc użycie tamtej reguły w interfejsie pokazywałoby przyciski, które serwer
 * i tak odrzuci — czyli dokładnie ten rodzaj rozjazdu, który zgłosił klient.
 *
 * ⚠️ Lustro `backend/src/modules/forms/form-editable.ts` — repo nie ma pakietu
 * współdzielonego, więc parytet jest ręczny. `RETURNED` jest edytowalny.
 */
export function formularzEdytowalnyWgStatusu(status: FormStatus): boolean {
  return ['DRAFT', 'SAVED', 'RETURNED'].includes(status);
}

/**
 * B-318 — czy to, co robi teraz użytkownik, jest POPRAWKĄ formularza już wysłanego,
 * a nie wypełnianiem szkicu.
 *
 * Rozstrzyga wyłącznie o SŁOWNICTWIE i układzie przycisków w edytorze: przy szkicu
 * główną akcją jest „Wyślij do zatwierdzenia", przy poprawce — „Zapisz zmiany",
 * a wysyłki nie ma wcale, bo nie ma czego wysyłać.
 *
 * Świadomie zbudowane NA `formularzEdytowalnyWgStatusu`, zamiast czwartej kopii
 * listy statusów: ten sam warunek stał już w trzech edytorach (generyczny,
 * kolizyjny, punkty socjalne), a lista rozjeżdżająca się między nimi to dokładnie
 * ta klasa błędu, którą opisuje nagłówek `bespokeFormCodes.ts`.
 */
export function jestPoprawkaPoWyslaniu(status?: FormStatus | null): boolean {
  return !!status && !formularzEdytowalnyWgStatusu(status);
}

/**
 * B-318 (ZUR-2026-0087): statusy, w których treść formularza jest ZAMROŻONA —
 * `REALIZED`, `CLOSED`, `CANCELLED`. Nie zmieni jej nikt, także administrator.
 *
 * Do 26.08 stało tu `if (userRole === 'ADMIN') return true`, więc panel pokazywał
 * administratorowi „Edytuj" na KAŻDYM formularzu — łącznie z zamkniętym, którego
 * serwer i tak nie zapisze. Klient trafił na to od strony zatwierdzonego
 * formularza: przycisk był, edytor się otwierał, a zapis odbijał się od bramki.
 *
 * Reguła brzegowa, której trzymamy się w obie strony: **panel nie może oferować
 * akcji, którą serwer odrzuci**. Serwerowi wolno być szerszym (i jest — pozwala
 * kierownikowi zapisać także cudzy szkic), ale nie odwrotnie.
 *
 * ⚠️ Lustro `backend/src/modules/forms/form-editable.ts` (`wolnoEdytowac`).
 */
const FORMULARZ_ZAMROZONY: FormStatus[] = ['REALIZED', 'CLOSED', 'CANCELLED'];

export function canEditSubmission(status: FormStatus, userRole: UserRole, isOwner: boolean): boolean {
  if (FORMULARZ_ZAMROZONY.includes(status)) return false;
  if (userRole === 'ADMIN') return true;
  if (userRole === 'DISPATCHER') return ['SUBMITTED', 'APPROVED'].includes(status);
  if (userRole === 'INSTRUCTOR' && isOwner) return ['DRAFT', 'SAVED', 'RETURNED'].includes(status);
  return false;
}

/**
 * SEC/§4 — role, które w ogóle ZAPISUJĄ formularze (zakładają, wysyłają, dodają notatki).
 * ANALYST (tylko odczyt) i AUTOMATION (bot) są poza listą: serwer odrzuca ich zapis na
 * trasie, więc panel nie może im go podsuwać (przycisk „Nowy formularz", notatki,
 * przeciąganie szkicu do „Wysłany" na tablicy).
 *
 * ⚠️ Lustro `ROLE_ZAPISU_FORMULARZY` w `backend/src/modules/forms/form-editable.ts` —
 * parytet pilnuje `scripts/__tests__/zapis-formularzy-role.test.mjs`.
 */
export const ROLE_ZAPISU_FORMULARZY: readonly UserRole[] = ['ADMIN', 'DISPATCHER', 'INSTRUCTOR'];

export function zapisujeFormularze(userRole?: UserRole | null): boolean {
  return !!userRole && ROLE_ZAPISU_FORMULARZY.includes(userRole);
}

/** Notatka do formularza: kierownik i administrator — do każdego, instruktor — do własnego.
 *  ⚠️ Lustro `wolnoKomentowac` w `backend/src/modules/forms/form-editable.ts`. */
export function wolnoKomentowac(userRole: UserRole | null | undefined, isOwner: boolean): boolean {
  if (userRole === 'ADMIN' || userRole === 'DISPATCHER') return true;
  if (userRole === 'INSTRUCTOR') return isOwner;
  return false;
}

/**
 * Czy panel ma zaoferować wysłanie tego szkicu (tablica: przeciągnięcie do „Wysłany").
 * Właściciel albo administrator — węższe niż serwer, który pozwala też kierownikowi
 * (pomiar 2026-09-29: cudze szkice wysyłał wyłącznie administrator). Rola bez zapisu —
 * nigdy, także we własnym (starym) dokumencie, bo serwer i tak ją odrzuci.
 */
export function canSubmitForm(status: FormStatus, userRole: UserRole, isOwner: boolean): boolean {
  if (!zapisujeFormularze(userRole)) return false;
  if (!isOwner && userRole !== 'ADMIN') return false;
  return ['DRAFT', 'SAVED', 'RETURNED'].includes(status);
}

/**
 * B-320 (ZUR-2026-0089): oświadczenie wystawia się **także ze szkicu** —
 * do momentu zatwierdzenia formularza.
 *
 * B-269 zawęziło to do samego `SUBMITTED`, żeby usunąć rozjazd między
 * zamrożonym dokumentem a dalej edytowanym szkicem. Klient zgłosił, że to
 * zawężenie zepsuło pracę w terenie, i sprostował intencję pierwotnego
 * zgłoszenia: chodziło o blokadę EDYCJI OŚWIADCZENIA po wciśnięciu „wystaw",
 * niezależnie od tego, czy raport został wysłany — a nie o to, kiedy wolno je
 * wystawić. Oświadczenie oddaje się drugiej stronie NA MIEJSCU ZDARZENIA,
 * a raport uzupełnia się później.
 *
 * Rozjazd nie jest już usuwany zakazem, tylko uwidaczniany: formularz niesie
 * plakietkę z numerem wydanego dokumentu, a jego usunięcie jest zablokowane.
 *
 * ⚠️ Lustro `backend/src/modules/forms/form-editable.ts` — parytet ręczny.
 */
export function oswiadczenieDozwoloneWgStatusu(status: FormStatus): boolean {
  return formularzEdytowalnyWgStatusu(status) || status === 'SUBMITTED';
}

/**
 * ⚠️ Powód `PRZED_WYSLANIEM` **zniknął w B-320**: po dopuszczeniu szkicu nie ma
 * już kiedy paść, a jego tekst („najpierw wyślij formularz") mówiłby wprost coś
 * odwrotnego do obowiązującej reguły. Został jeden powód — sprawa zamknięta.
 */
export type PowodBlokadyOswiadczenia = 'PO_ZAMKNIECIU';

export function powodBlokadyOswiadczeniaWgStatusu(status: FormStatus): PowodBlokadyOswiadczenia | null {
  return oswiadczenieDozwoloneWgStatusu(status) ? null : 'PO_ZAMKNIECIU';
}

/* ── B-319: format daty w POLACH FORMULARZA ─────────────────────────────────
 *
 * Wartość pola DATE/DATETIME jedzie do bazy w formacie aplikacji instruktora —
 * `DD.MM.RRRR` / `DD.MM.RRRR GG:MM` (kontrakt opisany w nagłówku
 * `pwa/src/components/FormFieldRenderer.tsx`). Natywne pola `date` i
 * `datetime-local` przyjmują WYŁĄCZNIE ISO, a wartość, której nie rozumieją,
 * pokazują jako PUSTE POLE — bez ostrzeżenia.
 *
 * Objaw zgłoszony przez właściciela 26.08: wejście w edycję zatwierdzonego
 * raportu awarii pokazywało pustą datę i godzinę zdarzenia, choć w dokumencie
 * siedziała. Zmierzone na stagingu: **13 z 22** wypełnionych raportów miało
 * format polski (pole puste), 9 — ISO (pole działało). Te 9 to dokumenty
 * dotknięte wcześniej w panelu, który zapisywał ISO: pole po cichu zmieniało
 * format przy każdej edycji, więc jedna rubryka trzymała dwa różne zapisy.
 */

/** Wartość z dokumentu → format przyjmowany przez natywne pole. Pusty napis,
 *  gdy nie da się rozpoznać — pole pokaże się puste, ale świadomie, a nie
 *  dlatego, że przeglądarka po cichu odrzuciła wartość. */
export function naPoleDaty(wartosc: unknown, zGodzina: boolean): string {
  if (typeof wartosc !== 'string' || !wartosc.trim()) return '';
  const d = parseAnyDate(wartosc);
  if (!d) return '';
  try { return format(d, zGodzina ? "yyyy-MM-dd'T'HH:mm" : 'yyyy-MM-dd'); } catch { return ''; }
}

/** Format natywnego pola → format zapisywany w dokumencie. Trzymamy zapis
 *  aplikacji instruktora, bo to ona wypełnia większość dokumentów, a mieszanie
 *  dwóch formatów w jednej rubryce psuje każde późniejsze sortowanie. */
export function zPolaDaty(wartosc: string, zGodzina: boolean): string {
  if (!wartosc) return '';
  const d = parseAnyDate(wartosc);
  if (!d) return wartosc;
  try { return format(d, zGodzina ? 'dd.MM.yyyy HH:mm' : 'dd.MM.yyyy'); } catch { return wartosc; }
}
